// Lib
import React from 'react';
import PropTypes from 'prop-types';
import dateformat from 'dateformat';
import { MenuItem } from '../../../node_module_clones/react-contextmenu';
import { useTranslation } from 'react-i18next';

// Utils
import friendlyTimeTranslated from '../../../common/utils/i18n/friendlyTimeTranslated';
import { getDisplayName, getId } from '../../../common/users/userHelper';
import { getTimestamp, TIMES } from '../../../common/utils/timeUtil';
import { useTranslator } from '../../components/i18n/useTranslator';

// Components
import FriendlyTimestamp from '../../components/datetime/FriendlyTimestamp';
import UserAvatar from '../../components/userAvatar/UserAvatar';

// Constants
import { USER_AVATAR_SIZES } from '../../../common/users/userConstants';

// Styles
import './ElementModificationContextMenuItems.scss';

const ElementModificationContextMenuItems = (props) => {
    const { currentUserId, createdUser, modifiedUser, createdTime, modifiedTime } = props;
    const { t } = useTranslation('common');
    const translate = useTranslator('canvas', 'contextMenuItems.modification');

    const createdUserId = getId(createdUser);
    const modifiedUserId = getId(modifiedUser);

    const createdAndModifiedBySameUser = createdUserId === modifiedUserId;

    const currentTime = getTimestamp();

    const createdTimeString = friendlyTimeTranslated({ t, timestamp: createdTime, fromTimestamp: currentTime });
    const modifiedTimeString = friendlyTimeTranslated({ t, timestamp: modifiedTime, fromTimestamp: currentTime });
    const modifiedCloseToCreationTime =
        modifiedTime - createdTime < 5 * TIMES.MINUTE || createdTimeString === modifiedTimeString;

    const createdTimeFullTimestamp = dateformat(createdTime, 'd mmm yyyy - HH:MM');
    const modifiedTimeFullTimestamp = dateformat(modifiedTime, 'd mmm yyyy - HH:MM');

    const isCreatedByCurrentUser = currentUserId === createdUserId;
    const isModifiedByCurrentUser = currentUserId === modifiedUserId;

    const createdUserName = getDisplayName(createdUser);
    const modifiedUserName = getDisplayName(modifiedUser);

    if (createdAndModifiedBySameUser && modifiedCloseToCreationTime) {
        return (
            <MenuItem disabled>
                <UserAvatar user={createdUser} size={USER_AVATAR_SIZES.REGULAR_SMALL} />
                <div className="detail">
                    {isCreatedByCurrentUser ? (
                        <div className="time-detail">{translate('createdByYou')}</div>
                    ) : (
                        <div className="time-detail">
                            {translate('createdBy', { userName: createdUserName })}
                        </div>
                    )}
                    <div className="time-detail">
                        <FriendlyTimestamp timestamp={modifiedTime} showSuffix title={modifiedTimeFullTimestamp} />
                    </div>
                </div>
            </MenuItem>
        );
    }

    if (createdAndModifiedBySameUser && !modifiedCloseToCreationTime) {
        const creationPrefix = createdTime > currentTime - 4 * TIMES.WEEK ? '' : translate('on');
        const modificationPrefix = modifiedTime > currentTime - 4 * TIMES.WEEK ? '' : translate('on');

        return (
            <MenuItem disabled>
                <UserAvatar user={createdUser} size={USER_AVATAR_SIZES.REGULAR_SMALL} />
                <div className="detail">
                    <div className="time-detail">
                        {isCreatedByCurrentUser
                            ? translate('createdByYou')
                            : translate('createdBy', { userName: createdUserName })}
                        {creationPrefix}&nbsp;
                        <FriendlyTimestamp timestamp={createdTime} showSuffix title={createdTimeFullTimestamp} />
                    </div>
                    <div className="time-detail">
                        {translate('lastModified')}&nbsp;
                        {modificationPrefix}&nbsp;
                        <FriendlyTimestamp timestamp={modifiedTime} showSuffix title={modifiedTimeFullTimestamp} />
                    </div>
                </div>
            </MenuItem>
        );
    }

    return (
        <>
            {createdUser && (
                <MenuItem disabled>
                    <UserAvatar user={createdUser} size={USER_AVATAR_SIZES.REGULAR_SMALL} />
                    <div className="detail">
                        <span>
                            {isCreatedByCurrentUser
                                ? translate('createdByYou')
                                : translate('createdBy', { userName: createdUserName })}
                        </span>
                        {createdTime && <FriendlyTimestamp timestamp={createdTime} title={createdTimeFullTimestamp} />}
                    </div>
                </MenuItem>
            )}
            {modifiedUser && (
                <MenuItem disabled>
                    <UserAvatar user={modifiedUser} size={USER_AVATAR_SIZES.REGULAR_SMALL} />
                    <div className="detail">
                        <span>
                            {isModifiedByCurrentUser
                                ? translate('modifiedByYou')
                                : translate('modifiedBy', { userName: modifiedUserName })}
                        </span>
                        {modifiedTime && (
                            <FriendlyTimestamp timestamp={modifiedTime} title={modifiedTimeFullTimestamp} />
                        )}
                    </div>
                </MenuItem>
            )}
        </>
    );
};

ElementModificationContextMenuItems.propTypes = {
    currentUserId: PropTypes.string,
    createdUser: PropTypes.object,
    modifiedUser: PropTypes.object,
    createdTime: PropTypes.number,
    modifiedTime: PropTypes.number,
};

export default ElementModificationContextMenuItems;
